/* eslint-disable @typescript-eslint/no-shadow -- disable shadowing for skeleton */
import { forwardRef, useEffect, useState } from "react";
import type { AboutCompany as AboutCompanyType } from "@repo/fetch-repository/aboutCompany";
import { AboutCompanyRepository } from "@repo/fetch-repository/aboutCompany";
import type { ComponentType } from "@repo/editor/types";
import type {
  ComponentSettings,
  ComponentSettingsProps,
} from "@repo/editor/externalComponents";
import * as t from "@rekajs/types";
import { useRouter } from "next/router";
import type { ErrorType } from "@repo/fetch-repository/factory";
import { useApp } from "@repo/app/useApp";
import { Skeleton } from "../components/ui/skeleton";
import { AboutCompanyItem } from "../components/pages/about-company/about-company-item";

interface AboutCompanyListProps {
  items: AboutCompanyType[];
}

export const aboutCompanyListDefinition: ComponentSettingsProps = {};

export const AboutCompanyList = forwardRef<
  HTMLDivElement,
  AboutCompanyListProps
>((_, ref): React.JSX.Element | null => {
  const router = useRouter();
  const app = useApp();
  const locale =
    app.rendererMode === "editor"
      ? (router.query.locale as string)
      : router.locale || "en";
  const [aboutCompanyItems, setAboutCompanyItems] = useState<
    AboutCompanyType[] | null
  >(null);
  const [error, setError] = useState<ErrorType | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    const repository: AboutCompanyRepository = new AboutCompanyRepository(
      locale
    );
    void repository
      .getMany()
      .then((res) => {
        if (res.succeeded && res.data) {
          setAboutCompanyItems(res.data);
        } else {
          setAboutCompanyItems(null);
          setError({
            message: res.errors?.message ?? "Unknown error",
            status: res.errors?.status ?? 500,
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [locale]);
  return (
    <div ref={ref}>
      {loading ? (
        <div className="w-full h-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-20">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              className="w-full h-full flex flex-col items-start justify-start gap-y-4"
              key={index}
            >
              <Skeleton className="w-full h-[123px] max-w-[123px] rounded-xl" />
              <Skeleton className="w-full h-10 rounded-xl" />
              {Array.from({ length: 3 }).map((_, index2) => (
                <Skeleton className="w-full h-4 rounded-xl" key={index2} />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="w-full h-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-20">
          {error ? (
            <div className="text-red-500">{error.message}</div>
          ) : (
            aboutCompanyItems?.map((item) => (
              <AboutCompanyItem item={item} key={item.id} />
            ))
          )}
        </div>
      )}
    </div>
  );
}) as ComponentType<AboutCompanyListProps>;
AboutCompanyList.displayName = "AboutCompanyList";

export const AboutCompanyListSettings: ComponentSettings = {
  name: "EAboutCompanyList",
  props: aboutCompanyListDefinition,
  label: "لیست درباره ما",
  childrenAllowed: false,
  widgets: {
    label: "لیست درباره ما",
    template: () =>
      t.componentTemplate({
        component: t.identifier({
          name: "EAboutCompanyList",
          external: true,
        }),
      }),
  },
};

AboutCompanyList.componentSettings = AboutCompanyListSettings;
